import { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Box, IconButton, Tooltip, Snackbar } from '@mui/material';
import { useInvestData } from '../hooks/useInvestData';
import { useFilteredDates } from '../hooks/useFilteredDates';
import { useDataProcessing } from '../hooks/useDataProcessing';
import ChartTypeSelector from './ChartTypeSelector';
import ChartRenderer from './ChartRenderer';
import RangeSelector from './RangeSelector';
import TradeModeSelector from './TradeModeSelector';
import ChartNotes from './ChartNotes';
import { chartTitles, chartConfigs } from '../utils/constants';
import JobModeSelector from './JobModeSelector';
import LaunchIcon from '@mui/icons-material/Launch';
import { Link, useNavigate } from 'react-router-dom';

// ── 月度圖表（日期只保留年月）────────────────────────────────────────────────
const MONTHLY_CHARTS = new Set(['usJobs', 'chinaPBOC', 'trade', 'rareEarth']);

// ── 月份→季度 ─────────────────────────────────────────────────────────────────
const M2Q = { '01':'第一季度','04':'第二季度','07':'第三季度','10':'第四季度' };

function fmtDate(dateStr, chartType) {
  if (!dateStr) return '';
  const s = String(dateStr);

  // 特殊處理 GDP：順手幫你加上「年」保持 UI 視覺一致 (例如 "2023年 Q1")
  if (chartType === 'usGDP') {
    return `${s.slice(0, 4)}年 ${M2Q[s.slice(5, 7)] ?? ''}`;
  }

  // 先依照原邏輯決定要截取的長度 (YYYY-MM 或是 YYYY-MM-DD / YYYY)
  const datePart = MONTHLY_CHARTS.has(chartType) ? s.slice(0, 7) : s.slice(0, 10);

  // 將字串依破折號拆分
  const parts = datePart.split('-');

  // 根據拆分出來的陣列長度，精準回傳對應的中文格式
  if (parts.length === 1) {
    return `${parts[0]}年`;                           // 處理 "YYYY"
  } else if (parts.length === 2) {
    return `${parts[0]}年${parts[1]}月`;              // 處理 "YYYY-MM"
  } else if (parts.length === 3) {
    return `${parts[0]}年${parts[1]}月${parts[2]}日`;  // 順手防呆處理 "YYYY-MM-DD"
  }

  // 兜底回傳
  return datePart;
}

// ── 增加一個判斷單位是否為「人」的輔助函數
function isPersonUnit(name) {
  return UNITS[name] === '人';
}

// ── 修改後的 fmt 函數
function fmt(val, unitName) {
  if (val == null || val === '' || isNaN(Number(val))) return 'N/A';
  
  // 如果是「人」的單位，強制取整數，不保留小數
  if (unitName && isPersonUnit(unitName)) {
    return Math.round(Number(val)).toString();
  }
  
  // 其他單位保留兩位小數
  return Number(val).toFixed(2);
}

// ── 計量單位 ──────────────────────────────────────────────────────────────────
const UNITS = {
  '人民幣貸款':'兆人民幣', '委託貸款':'兆人民幣', '企業債券':'兆人民幣', '政府債券':'兆人民幣', '股票':'兆人民幣', '其他':'兆人民幣',
  '內部持有':'美元', '公眾持有':'美元',
  '非農就業人數':'千人', '失業率':'%',
  'ADP私營就業人數變化':'人',
  'WTI原油':'美元/桶', '黃金':'美元/盎司', '比特幣':'美元',
  'DXY美元指數':'', '道瓊指數':'點', 'VIX恐慌指數':'', '10年期美債':'%',
  // ✅ 補上 keyAssets 與 freedom 的項目單位
  '台積電':'美元每股', '英偉達':'美元每股', '阿斯麥':'美元每股',
  '標普500':'點', '台灣加權指數':'點'
};
function u(name) { const unit = UNITS[name]; return unit ? ` ${unit}` : ''; }

// ── 取得最近五期資料 ──────────────────────────────────────────────────────────
function getLast5Rows(chartType, chartConfig, filteredDates, data, debtSeries, tradeSeries, chartData, tradeMode, jobMode) {
  try {
    const last5Dates = (filteredDates || []).slice(-5);

    // trade
    if (chartType === 'trade' && Array.isArray(tradeSeries) && tradeSeries.length > 0) {
      const dataLen = tradeSeries[0].data.length;

      // 💡 新增：根據 tradeMode 決定說明的字尾
      let modeSuffix = '';
      if (tradeMode === 'import') modeSuffix = '美國從中國進口';
      else if (tradeMode === 'export') modeSuffix = '美國出口到中國';
      else modeSuffix = '美國與中國雙邊貨物貿易'; // 綜合模式

      return last5Dates.map((date, i) => {
        const idx = dataLen - 5 + i;
        const parts = tradeSeries.map(s => {
          const label = s.label ?? s.name ?? '未知';
          const unit = u(label) || ' 美元';
          return `${label} ${fmt(s.data?.[idx])}${unit}`;
        });
        
        // 💡 修正：在這裡把原本的日期加上字尾 (例如 "2026年03月進口")
        return { date: `${fmtDate(date, chartType)}${modeSuffix}`, parts };
      });
    }

    // chinaPBOC — 扁平 { date, RMB_Loans, ... }
    if (chartType === 'chinaPBOC' && Array.isArray(debtSeries) && debtSeries.length > 0) {
      const fields = { RMB_Loans:'人民幣貸款', Entrusted:'委託貸款', Corp_Bond:'企業債券', Gov_Bond:'政府債券', Equity:'股票' };
      return debtSeries.slice(-5).map(item => ({
        date: fmtDate(item.date, chartType),
        parts: Object.entries(fields).map(([k, label]) => `${label} ${fmt(item[k])}${u(label)}`),
      }));
    }

    // usDebt — 扁平 { date, DEBT_INTRA, DEBT_PUB }
    if (chartType === 'usDebt' && Array.isArray(debtSeries) && debtSeries.length > 0) {
      return debtSeries.slice(-5).map(item => {
        // 💡 新增：將內部持有與公眾持有相加取得總額（加上 Number 防呆）
        const totalDebt = Number(item.DEBT_INTRA || 0) + Number(item.DEBT_PUB || 0);
        
        return {
          date: fmtDate(item.date, chartType),
          parts: [
            `內部持有 ${fmt(item.DEBT_INTRA)}${u('內部持有')}`,
            `公眾持有 ${fmt(item.DEBT_PUB)}${u('公眾持有')}`,
            // 💡 新增：展示國債總額，單位會自動動態同步
            `國債總額 ${fmt(totalDebt)}${u('公眾持有')}`,
          ],
        };
      });
    }

    // usGDP — { date, name, value }[]，季首日
    if (chartType === 'usGDP' && Array.isArray(data) && data.length > 0) {
      const allDates = [...new Set(data.map(d => d.date))].sort();
      const last5 = allDates.slice(-5);

      const gdpNameMap = {
        'GDPC1': '實質GDP',
        'PCEC': '個人消費支出',
        'GPDI': '私人投資',
        'GCE': '政府支出',
        'EXPGS': '出口總額',
        'IMPGS': '進口總額',
        'GDP': '名目GDP'
      };

      // 💡 依據要求定義嚴格的排序順序：名目GDP -> C -> I -> G -> X -> M -> 實質GDP
      const gdpOrder = ['GDP', 'PCEC', 'GPDI', 'GCE', 'EXPGS', 'IMPGS', 'GDPC1'];

      return last5.map(date => ({
        date: fmtDate(date, 'usGDP'),
        parts: data
          .filter(d => d.date === date)
          // 💡 新增：自訂排序邏輯，保證完全符合公式順序
          .sort((a, b) => {
            const indexA = gdpOrder.indexOf(a.name);
            const indexB = gdpOrder.indexOf(b.name);
            // 防呆：若遇到未定義的 key 則排到最後面
            const weightA = indexA === -1 ? 999 : indexA;
            const weightB = indexB === -1 ? 999 : indexB;
            return weightA - weightB;
          })
          .map(d => {
            const chineseName = gdpNameMap[d.name] ?? d.name;
            const rawValue = parseFloat(d.value) || 0;
            const trillionValue = (rawValue / 1000).toFixed(2);
            return `${chineseName} ${trillionValue} 兆美元`;
          }),
      }));
    }

    // usJobs 年度聚合
    if (chartType === 'usJobs' && Array.isArray(chartData) && chartData.length > 0 && chartData[0].year !== undefined) {
      return chartData.slice(-5).map(d => ({
        date: `${String(d.year ?? d.date)}年`,
        parts: [
          // ✅ 根據 jobMode 動態切換顯示大非農或小非農
          jobMode === 'smallJob' 
            ? `小非農(ADP) ${fmt(d.employment, 'ADP私營就業人數變化')}${u('ADP私營就業人數變化')}`
            : `非農就業 ${fmt(d.employment, '非農就業人數')}${u('非農就業人數')}`,
          d.unemployment != null ? `失業率 ${d.unemployment}%` : null,
        ].filter(Boolean),
      }));
    }

    // usJobs 月度
    if (chartType === 'usJobs' && Array.isArray(data) && data.length > 0 && last5Dates.length > 0) {
      return last5Dates.map(date => {
        const jobs  = data.find(d => d.date === date && d.name === '非農就業人數');
        const unemp = data.find(d => d.date === date && d.name === '失業率');
        const adp   = data.find(d => d.date === date && d.name === 'ADP私營就業人數變化');
        return { date: fmtDate(date, chartType), parts: [
          `非農就業 ${fmt(jobs?.value, '非農就業人數')}${u('非農就業人數')}`,
          `失業率 ${fmt(unemp?.value, '失業率')}%`,
          `小非農(ADP) ${fmt(adp?.value, 'ADP私營就業人數變化')}${u('ADP私營就業人數變化')}`,
        ]};
      });
    }

    // rareEarth — { date, name, value }[] 格式（與後端一致）
    if (chartType === 'rareEarth' && Array.isArray(data) && data.length > 0) {
      const allDates = [...new Set(data.map(d => d.date))].sort();
      const last5 = allDates.slice(-5);
      return last5.map(date => {
        const items = data.filter(d => d.date === date);
        const parts = items.map(d => `${d.name} ${fmt(d.value)} 美元`);
        return { date: fmtDate(date, chartType), parts };
      });
    }

    // dualLine — 日頻 { date, name, value }[]
    if (chartConfig?.type === 'dualLine' && Array.isArray(data) && data.length > 0) {
      const nameMap = {
        oil:          { l:'10年期美債', r:'WTI原油' },
        gold:         { l:'10年期美債', r:'黃金' },
        bitcoin:      { l:'10年期美債', r:'比特幣' },
        usd:          { l:'10年期美債', r:'DXY美元指數' },
        usStocks:     { l:'10年期美債', r:'道瓊指數' },
        stockGold:    { l:'道瓊指數',   r:'黃金' },
        stockBitcoin: { l:'道瓊指數',   r:'比特幣' },
        sentiment:    { l:'VIX恐慌指數',r:'道瓊指數' },
      };
      const nm = nameMap[chartType] ?? {};
      const lLabel = chartConfig.leftLabel  ?? nm.l ?? '左軸';
      const rLabel = chartConfig.rightLabel ?? nm.r ?? '右軸';
      return last5Dates.map(date => {
        const left  = data.find(d => d.date === date && d.name === nm.l);
        const right = data.find(d => d.date === date && d.name === nm.r);
        return { date: fmtDate(date, chartType), parts: [
          `${lLabel} ${fmt(left?.value)}${u(nm.l)}`,
          right ? `${rLabel} ${fmt(right?.value)}${u(nm.r)}` : null,
        ].filter(Boolean)};
      });
    }

    // line / keyAssets / freedom — 日頻 { date, name, value }[]
    if ((chartConfig?.type === 'line' || chartType === "freedom") && Array.isArray(data) && data.length > 0 && data[0]?.name) {
      return last5Dates.map(date => {
        const items = data.filter(d => d.date === date);
        return { date: fmtDate(date, chartType), parts: items.map(d => `${d.name} ${fmt(d.value)}${u(d.name)}`) };
      });
    }

  } catch (_) { return null; }
  return null;
}

// ── SEO 五期展示區塊 ──────────────────────────────────────────────────────────
function SeoDataBlock({ chartType, filteredDates, data, debtSeries, tradeSeries, chartData, tradeMode, jobMode }) {
  const chartConfig = chartConfigs[chartType];
  const chartTitle  = chartTitles[chartType] ?? chartType;
  const rows = getLast5Rows(chartType, chartConfig, filteredDates, data, debtSeries, tradeSeries, chartData, tradeMode, jobMode);
  if (!rows?.length) return null;

  return (
    <Box component="section" aria-label={`${chartTitle}最新數據`}
      sx={{ mt: 1, p: 1.5, border: '1px dashed #ccc', borderRadius: 1, backgroundColor: 'rgba(0,0,0,0.02)' }}
    >
      <Typography variant="caption" component="p"
        sx={{ color: 'text.secondary', fontSize: '0.75rem', lineHeight: 2, whiteSpace: 'pre-line' }}
      >
        {`${chartTitle} 最新數據（近五期）\n` +
          rows.map(r => `${r.date}：${r.parts.join('，')}`).join('\n')}
      </Typography>
    </Box>
  );
}

function getDefaultRange(chartType) {
  if (chartType === 'usGDP') return 365 * 3 + 31;
  if (MONTHLY_CHARTS.has(chartType)) return 365 + 31;
  return 30;
}

// ── 主組件（與你原版完全一致）────────────────────────────────────────────────
export default function Invest({ initialChartType = "keyAssets" }) {
  // 在主組件內加一個狀態
  const [copySnackbar, setCopySnackbar] = useState(false);
  // ✅ 根據圖表類型決定預設 range
  const defaultRange = getDefaultRange(initialChartType);
  const navigate = useNavigate();
  const [range, setRange] = useState(defaultRange);
  const [chartType, setChartType] = useState(initialChartType);
  const [tradeMode, setTradeMode] = useState("combined");
  const [jobMode, setJobMode] = useState("bigJob");
  const [leftAxis, setLeftAxis] = useState(0);
  const [rightAxis, setRightAxis] = useState(1);

  const freecode = useMemo(() => `1${leftAxis}${rightAxis}`, [leftAxis, rightAxis]);

  const { data, forexData, loading, initialized, fetchData } = useInvestData();
  const filteredDates = useFilteredDates(chartType, data, range);
  const { dualLineSeries, tradeSeries, debtSeries, chartData, transformed } = useDataProcessing(
    chartType, data, range, filteredDates, tradeMode, jobMode
  );

  useEffect(() => {
    if (!chartType || !range) return;
    if (!initialized) {
      fetchData(chartType, range, null);
      return;
    }
    if (chartType === "freedom") {
      const id = setTimeout(() => fetchData(chartType, range, freecode), 300);
      return () => clearTimeout(id);
    }
    fetchData(chartType, range, freecode);
  }, [chartType, range, freecode, initialized, fetchData]);

  const handleAxisChange      = useCallback((axis, value) => { if (axis === 'left') setLeftAxis(value); else if (axis === 'right') setRightAxis(value); }, []);
  const handleFreecodeChange  = useCallback(() => {}, []);
  const handleChartTypeChange = useCallback((newType) => { setChartType(newType); navigate(`/chart/${newType}`, { replace: true }); }, [navigate]);
  const handleRangeReset      = useCallback((v) => setRange(v), []);
  const handleRangeChange     = useCallback((v) => setRange(v), [range]);
  const handleTradeModeChange = useCallback((v) => setTradeMode(v), []);
  const handleJobModeChange   = useCallback((v) => setJobMode(v), []);
  const handleTradeModeReset  = useCallback((v) => setTradeMode(v), []);

  return (
    <div style={{ width: '100%', maxWidth: '80rem', margin: '0 auto', padding: '16px', position: 'relative' }}>
      <ChartTypeSelector chartType={chartType} onChartTypeChange={handleChartTypeChange} onRangeReset={handleRangeReset} />

      <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={2}>
        <Typography variant="h4" align="center" sx={{
          fontWeight: 700, fontFamily: "Noto Sans SC, sans-serif",
          letterSpacing: "0.05em", lineHeight: 1.2,
          background: "linear-gradient(90deg, #afafacff, #383730ff)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          textShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}>
          {chartTitles[chartType]}
        </Typography>
        
        <Tooltip title="取得此圖表專屬獨立連結" placement="right">
          <IconButton
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/chart/${chartType}`);
              setCopySnackbar(true);
            }}
            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
          >
            <LaunchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <ChartRenderer
        chartType={chartType} loading={loading} data={data} forexData={forexData}
        filteredDates={filteredDates} dualLineSeries={dualLineSeries} chartData={chartData}
        tradeSeries={tradeSeries} debtSeries={debtSeries} tradeMode={tradeMode} jobMode={jobMode}
        range={range} leftAxis={leftAxis} rightAxis={rightAxis}
        onAxisChange={handleAxisChange} onFreecodeChange={handleFreecodeChange}
      />

      {chartType === "trade"  && <TradeModeSelector tradeMode={tradeMode} onTradeModeChange={handleTradeModeChange} range={range} />}
      {chartType === "usJobs" && <JobModeSelector jobMode={jobMode} onJobModeChange={handleJobModeChange} />}

      <RangeSelector chartType={chartType} range={range} onRangeChange={handleRangeChange} onTradeModeReset={handleTradeModeReset} />

      <ChartNotes chartType={chartType} range={range} />
      <SeoDataBlock chartType={chartType} filteredDates={filteredDates} data={data} debtSeries={debtSeries} tradeSeries={tradeSeries} chartData={chartData} tradeMode={tradeMode} jobMode={jobMode} />
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message="圖表連結已複製"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </div>
  );
}